import React, { useCallback, useEffect, useState } from "react";
import { remediationsApi } from "../../api/endpoints";
import { AIDialog } from "../ai/AIDialog";
import {
  REMEDIATION_STATUS_LABELS,
  RemediationChip,
} from "./RemediationChip";

/**
 * RemediationDialog — inline form for a finding's remediation.
 *
 * Reuses the AIDialog primitive for the same a11y contract as the
 * ExplainFindingDialog and ValidationSuggestionDialog: Enter/Space open,
 * Escape closes + restores focus, Tab trap, `role="dialog"` + `aria-modal`.
 *
 * Contract:
 *   * The deterministic finding is NEVER modified by this dialog —
 *     remediation is workspace-scoped metadata layered on top.
 *   * On open, refetch current state so the chip always reflects
 *     backend truth (same pattern as ExplainFindingDialog).
 *   * The form only exposes fields the acting role can edit — but the
 *     backend is authoritative. Attempts to reassign owner as an
 *     analyst are refused with an inline error.
 */
const STATUSES = ["under_review", "accepted", "treatment_planned", "resolved"];


export function RemediationDialog({
  workspaceId,
  runId,
  finding,
  currentStatus = "open",
  currentRemediation = null,
  onSaved,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const [state, setState] = useState({
    status: currentStatus === "open" ? "under_review" : currentStatus,
    notes: currentRemediation?.notes || "",
    target_date: currentRemediation?.target_date || "",
  });

  // Refresh when currentRemediation identity changes (e.g. after save).
  useEffect(() => {
    setState({
      status:
        (currentRemediation?.status ??
          (currentStatus === "open" ? "under_review" : currentStatus)),
      notes: currentRemediation?.notes || "",
      target_date: currentRemediation?.target_date || "",
    });
  }, [currentRemediation, currentStatus]);

  const testIdPrefix = `remediation-${finding.id}`;

  const submit = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const body = {
        status: state.status,
        notes: state.notes || null,
        target_date: state.target_date || null,
      };
      const saved = await remediationsApi.upsertForFinding(
        workspaceId, finding.id, body,
      );
      onSaved?.(saved);
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  }, [state, workspaceId, finding.id, onSaved]);

  return (
    <AIDialog
      testIdPrefix={testIdPrefix}
      ariaLabel={`Manage remediation for finding ${finding.rule_id}`}
      renderTrigger={({ triggerRef, onClick, onKeyDown, ariaExpanded }) => (
        <RemediationChip
          triggerRef={triggerRef}
          onClick={onClick}
          onKeyDown={onKeyDown}
          ariaExpanded={ariaExpanded}
          status={currentStatus}
          testId={`remediation-trigger-${finding.id}`}
        />
      )}
      renderHeader={() => (
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-widest text-slate-500">
            Remediation
          </div>
          <h2 className="mt-0.5 truncate text-sm font-medium text-slate-900">
            {finding.rule_id} · v{finding.rule_version}
          </h2>
          <div className="mt-1 flex items-center gap-2 text-[10px] text-slate-500">
            <span>Finding: <code>{finding.id.slice(0, 8)}</code></span>
            {currentRemediation?.owner_user_id && (
              <span>· Owner: <code>{currentRemediation.owner_user_id.slice(0, 8)}</code></span>
            )}
          </div>
        </div>
      )}
      renderBody={() => (
        <form
          data-testid={`${testIdPrefix}-form`}
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <fieldset>
            <legend className="text-[11px] uppercase tracking-widest text-slate-500">
              Status
            </legend>
            <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {STATUSES.map((s) => (
                <label
                  key={s}
                  data-testid={`${testIdPrefix}-status-${s}`}
                  className={
                    "flex cursor-pointer items-center gap-2 rounded border px-2 py-1.5 " +
                    "text-xs text-slate-800 focus-within:ring-2 focus-within:ring-slate-400 " +
                    (state.status === s
                      ? "border-slate-500 bg-slate-50 font-medium"
                      : "border-slate-200 bg-white")
                  }
                >
                  <input
                    type="radio"
                    name="status"
                    value={s}
                    checked={state.status === s}
                    onChange={() => setState((prev) => ({ ...prev, status: s }))}
                    className="h-3 w-3"
                  />
                  {REMEDIATION_STATUS_LABELS[s]}
                </label>
              ))}
            </div>
          </fieldset>

          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-slate-500">
              Target date (optional)
            </span>
            <input
              type="date"
              value={state.target_date || ""}
              onChange={(e) =>
                setState((p) => ({ ...p, target_date: e.target.value }))
              }
              data-testid={`${testIdPrefix}-target-date`}
              className="mt-1 block w-full rounded border border-slate-300 px-2 py-1.5 text-sm text-slate-900 focus:border-slate-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
            />
          </label>

          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-slate-500">
              Notes
            </span>
            <textarea
              rows={4}
              value={state.notes}
              onChange={(e) =>
                setState((p) => ({ ...p, notes: e.target.value }))
              }
              placeholder="What action, when, evidence."
              data-testid={`${testIdPrefix}-notes`}
              className="mt-1 block w-full rounded border border-slate-300 px-2 py-1.5 text-sm text-slate-900 focus:border-slate-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
              maxLength={4000}
            />
          </label>

          {error && (
            <div
              data-testid={`${testIdPrefix}-error`}
              className="rounded border border-red-200 bg-red-50 p-2 text-xs text-red-800"
            >
              {error?.body?.error?.message || "Something went wrong."}
            </div>
          )}

          <div className="flex items-center justify-end gap-2">
            <button
              type="submit"
              disabled={saving}
              data-testid={`${testIdPrefix}-submit`}
              className="inline-flex items-center gap-1 rounded border border-slate-800 bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500"
            >
              {saving ? "Saving…" : "Save remediation"}
            </button>
          </div>
        </form>
      )}
      renderFooter={() =>
        currentRemediation?.updated_at ? (
          <>Last updated <time dateTime={currentRemediation.updated_at}>{currentRemediation.updated_at}</time></>
        ) : (
          <>Remediation is workspace metadata — the deterministic finding is unchanged.</>
        )
      }
    />
  );
}
